/**
 * aggregate-v1 cache (Lightly Instant Experience — privacy slice).
 *
 * Fail-closed, allowlist-based cache for the attendance dashboard. We NEVER clone
 * the server response and remove fields. Instead we CONSTRUCT a brand-new object
 * containing only an explicit allowlist of non-identifying aggregate data, tagged
 * with a schema version and ownership (organization + authenticated user +
 * permission capability). On read we accept only entries that match the schema
 * AND the current owner exactly; anything else is rejected and deleted.
 *
 * Detailed roster data is NEVER persisted — it is memory-only and re-fetched.
 *
 * ─────────────────────────── aggregate-v1 schema ───────────────────────────
 * {
 *   cacheSchema: "aggregate-v1",                 // exact string
 *   cacheOwner: { orgId: string, userId: string, capability: string },
 *   cachedAt: number,                            // epoch ms
 *   data?: {                                     // summary aggregate (counts + service metadata)
 *     ok?, orgId?, serviceId?, serviceTitle?, serviceLabel?, serviceType?,
 *     serviceStartsAt?, serviceStartsAtIso?, serviceCheckinOpenAt?,
 *     serviceCheckinOpenAtIso?, serviceCheckinCloseAt?, serviceCheckinCloseAtIso?,
 *     serviceTimingCategory?, serviceMonth?,
 *     counts?: { [countName: string]: number }   // dynamic keys, NUMBER values only
 *   },
 *   services?: Array<{                           // fixed-shape service metadata, scalars only
 *     serviceId?, id?, title?, serviceTitle?, label?, serviceLabel?, serviceType?,
 *     startsAt?, serviceStartsAt?, startsAtIso?, serviceStartsAtIso?,
 *     checkinOpenAt?, serviceCheckinOpenAt?, checkinOpenAtIso?,
 *     checkinCloseAt?, serviceCheckinCloseAt?, checkinCloseAtIso?,
 *     serviceCheckinCloseAt?, status?, timingCategory?, serviceTimingCategory?,
 *     archived?, checkinCount?, hasAttendanceData?
 *   }>,
 *   selectedServiceId?: string
 * }
 * No other top-level, data, counts, or service keys are permitted. No arrays of
 * person records. Any unknown path fails validation.
 */

export const CACHE_SCHEMA = "aggregate-v1";
export const SENSITIVE_CACHE_PREFIX = "attendance_dashboard";

const TOP_FIELDS = new Set([
  "cacheSchema",
  "cacheOwner",
  "cachedAt",
  "data",
  "services",
  "selectedServiceId",
]);

const OWNER_FIELDS = ["orgId", "userId", "capability"];
const OWNER_FIELD_SET = new Set(OWNER_FIELDS);

// Exact approved aggregate count names (mirrors normalizeCounts in
// AttendanceDashboardPage). Any other count key (incl. a person id) is rejected.
const COUNT_KEYS = [
  "present",
  "newToday",
  "missed",
  "drifting",
  "returningGuests",
  "totalPeople",
  "totalCheckins",
  "membersPresent",
  "guestsPresent",
  "childrenPresent",
  "youthPresent",
  "householdsPresent",
  "menPresent",
  "womenPresent",
  "genderNotProvidedPresent",
  "birthdaysThisMonth",
  "anniversariesThisMonth",
];
const COUNT_KEY_SET = new Set(COUNT_KEYS);

const SUMMARY_FIELDS = [
  "ok",
  "orgId",
  "serviceId",
  "serviceTitle",
  "serviceLabel",
  "serviceType",
  "serviceStartsAt",
  "serviceStartsAtIso",
  "serviceCheckinOpenAt",
  "serviceCheckinOpenAtIso",
  "serviceCheckinCloseAt",
  "serviceCheckinCloseAtIso",
  "serviceTimingCategory",
  "serviceMonth",
];
const SUMMARY_FIELD_SET = new Set([...SUMMARY_FIELDS, "counts"]);

const SERVICE_FIELDS = [
  "serviceId",
  "id",
  "title",
  "serviceTitle",
  "label",
  "serviceLabel",
  "serviceType",
  "startsAt",
  "serviceStartsAt",
  "startsAtIso",
  "serviceStartsAtIso",
  "checkinOpenAt",
  "serviceCheckinOpenAt",
  "checkinOpenAtIso",
  "serviceCheckinOpenAtIso",
  "checkinCloseAt",
  "serviceCheckinCloseAt",
  "checkinCloseAtIso",
  "serviceCheckinCloseAtIso",
  "status",
  "timingCategory",
  "serviceTimingCategory",
  "archived",
  "checkinCount",
  "hasAttendanceData",
];
const SERVICE_FIELD_SET = new Set(SERVICE_FIELDS);

const isScalar = (v) =>
  v === null ||
  typeof v === "string" ||
  typeof v === "number" ||
  typeof v === "boolean";

export function isSensitiveCacheKey(key) {
  return String(key || "").startsWith(SENSITIVE_CACHE_PREFIX);
}

export function buildOwner(orgId, userId, capability) {
  return {
    orgId: String(orgId || ""),
    userId: String(userId || ""),
    capability: String(capability || ""),
  };
}

export function ownerMatches(a, b) {
  return (
    !!a &&
    !!b &&
    String(a.orgId) === String(b.orgId) &&
    String(a.userId) === String(b.userId) &&
    String(a.capability) === String(b.capability)
  );
}

// Category of an ownership mismatch (never the values). null = match.
export function ownerMismatchReason(entryOwner, currentOwner) {
  if (!currentOwner) return null;
  if (!validOwner(entryOwner)) return "malformed-owner";
  if (String(entryOwner.orgId) !== String(currentOwner.orgId)) return "orgId";
  if (String(entryOwner.userId) !== String(currentOwner.userId))
    return "userId";
  if (String(entryOwner.capability) !== String(currentOwner.capability))
    return "capability";
  return null;
}

// The single source of truth for the aggregate-v1 shape. Exported so browser
// acceptance uses the SAME constants as production validation (no transcription).
export const AGGREGATE_SCHEMA = Object.freeze({
  schema: CACHE_SCHEMA,
  top: Object.freeze([...TOP_FIELDS]),
  owner: Object.freeze([...OWNER_FIELDS]),
  summary: Object.freeze([...SUMMARY_FIELD_SET]),
  counts: Object.freeze([...COUNT_KEYS]),
  service: Object.freeze([...SERVICE_FIELDS]),
});

// ---- allowlist constructors (build NEW objects; never clone-and-strip) ----
function pickCounts(counts) {
  const out = {};
  if (counts && typeof counts === "object" && !Array.isArray(counts)) {
    for (const [k, v] of Object.entries(counts)) {
      if (COUNT_KEY_SET.has(k) && typeof v === "number" && Number.isFinite(v)) {
        out[k] = v;
      }
    }
  }
  return out;
}

function pickSummary(data) {
  if (!data || typeof data !== "object" || Array.isArray(data))
    return undefined;
  const out = {};
  for (const f of SUMMARY_FIELDS) {
    if (data[f] !== undefined && isScalar(data[f])) out[f] = data[f];
  }
  out.counts = pickCounts(data.counts);
  return out;
}

function pickServiceList(services) {
  if (!Array.isArray(services)) return undefined;
  return services.map((svc) => {
    const out = {};
    if (svc && typeof svc === "object" && !Array.isArray(svc)) {
      for (const f of SERVICE_FIELDS) {
        if (svc[f] !== undefined && isScalar(svc[f])) out[f] = svc[f];
      }
    }
    return out;
  });
}

/** Construct a fresh aggregate-v1 entry from an explicit allowlist. */
export function buildAggregateEntry(owner, body = {}, cachedAt) {
  const entry = {
    cacheSchema: CACHE_SCHEMA,
    cacheOwner: buildOwner(owner?.orgId, owner?.userId, owner?.capability),
    cachedAt: typeof cachedAt === "number" ? cachedAt : 0,
  };
  const data = pickSummary(body.data);
  if (data) entry.data = data;
  const services = pickServiceList(body.services);
  if (services) entry.services = services;
  if (typeof body.selectedServiceId === "string") {
    entry.selectedServiceId = body.selectedServiceId;
  }
  return entry;
}

// ---- strict, path-aware validation (fail closed) ----
function validCounts(counts) {
  if (!counts || typeof counts !== "object" || Array.isArray(counts))
    return false;
  return Object.entries(counts).every(
    ([k, v]) =>
      COUNT_KEY_SET.has(k) && typeof v === "number" && Number.isFinite(v),
  );
}

function validSummary(data) {
  if (data === undefined) return true;
  if (!data || typeof data !== "object" || Array.isArray(data)) return false;
  for (const [k, v] of Object.entries(data)) {
    if (!SUMMARY_FIELD_SET.has(k)) return false;
    if (k === "counts") {
      if (!validCounts(v)) return false;
    } else if (!isScalar(v)) {
      return false;
    }
  }
  return true;
}

function validOwner(owner) {
  if (!owner || typeof owner !== "object" || Array.isArray(owner)) return false;
  // exactly the three owner fields, each a string
  const keys = Object.keys(owner);
  if (keys.length !== OWNER_FIELDS.length) return false;
  return (
    OWNER_FIELDS.every(
      (f) => typeof owner[f] === "string" && OWNER_FIELD_SET.has(f),
    ) && keys.every((k) => OWNER_FIELD_SET.has(k))
  );
}

function validServices(services) {
  if (services === undefined) return true;
  if (!Array.isArray(services)) return false;
  return services.every((svc) => {
    if (!svc || typeof svc !== "object" || Array.isArray(svc)) return false;
    return Object.entries(svc).every(
      ([k, v]) => SERVICE_FIELD_SET.has(k) && isScalar(v),
    );
  });
}

export function validateAggregateEntry(parsed, owner) {
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
    return false;
  // required fields + types
  if (parsed.cacheSchema !== CACHE_SCHEMA) return false;
  if (typeof parsed.cachedAt !== "number" || !Number.isFinite(parsed.cachedAt))
    return false;
  if (!validOwner(parsed.cacheOwner)) return false;
  // no unexpected top-level keys
  if (!Object.keys(parsed).every((k) => TOP_FIELDS.has(k))) return false;
  // ownership match (synchronous tenant/user/capability boundary)
  if (owner && !ownerMatches(parsed.cacheOwner, owner)) return false;
  if (!validSummary(parsed.data)) return false;
  if (!validServices(parsed.services)) return false;
  if (
    parsed.selectedServiceId !== undefined &&
    typeof parsed.selectedServiceId !== "string"
  )
    return false;
  return true;
}

// ---- storage helpers ----
function resolveStorage(storage) {
  if (storage) return storage;
  if (typeof window !== "undefined" && window.localStorage)
    return window.localStorage;
  return null;
}

export function writeAggregate(key, owner, body, cachedAt, storage) {
  const store = resolveStorage(storage);
  if (!store) return;
  // Do not persist with a half-resolved owner (no authenticated user yet); an
  // empty userId would never match the warm read and would just churn.
  if (!owner || !owner.userId) return;
  try {
    const at = typeof cachedAt === "number" ? cachedAt : nowMs();
    store.setItem(key, JSON.stringify(buildAggregateEntry(owner, body, at)));
  } catch {
    /* ignore quota / serialization failures */
  }
}

function nowMs() {
  return typeof Date !== "undefined" ? Date.now() : 0;
}

/**
 * Read + validate an aggregate-v1 entry for `owner`. Rejects and DELETES entries
 * with a missing/old/unknown schema, an ownership mismatch, an unexpected path,
 * or that are stale beyond `maxAgeMs`. Returns { cachedAt, data, services,
 * selectedServiceId } or null.
 */
export function readAggregate(key, owner, maxAgeMs = null, storage) {
  const store = resolveStorage(storage);
  if (!store) return null;
  const raw = store.getItem(key);
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    store.removeItem(key);
    return null;
  }
  if (!validateAggregateEntry(parsed, owner)) {
    store.removeItem(key);
    return null;
  }
  if (
    maxAgeMs &&
    parsed.cachedAt &&
    nowMs() - Number(parsed.cachedAt) > maxAgeMs
  ) {
    store.removeItem(key);
    return null;
  }
  return {
    cachedAt: parsed.cachedAt,
    data: parsed.data,
    services: parsed.services,
    selectedServiceId: parsed.selectedServiceId,
  };
}

/**
 * Proactively remove schema-valid entries whose owner does NOT match `owner`
 * (cross-user / cross-capability / cross-org). Call once the current owner is
 * fully resolved, so foreign entries don't merely "persist until read".
 * Malformed/legacy entries are handled by purgeLegacyPiiCaches.
 */
export function purgeForeignOwnerCaches(owner, storage) {
  const store = resolveStorage(storage);
  if (!store || !owner || !owner.userId) return 0;
  const keys = [];
  for (let i = 0; i < store.length; i += 1) {
    const k = store.key(i);
    if (isSensitiveCacheKey(k)) keys.push(k);
  }
  let removed = 0;
  for (const key of keys) {
    let parsed = null;
    try {
      parsed = JSON.parse(store.getItem(key));
    } catch {
      continue; // legacy purge handles unparseable
    }
    if (
      validateAggregateEntry(parsed, null) &&
      !ownerMatches(parsed.cacheOwner, owner)
    ) {
      store.removeItem(key);
      removed += 1;
    }
  }
  return removed;
}

// ─────────────────── safe diagnostic (preview/dev only) ───────────────────
// Uses the SAME validators as production cache reads. Returns only aggregate
// counts, issue-code categories, and ownership booleans — never cache values,
// keys, org/user ids, capabilities, or member/attendance data.

// Granular, value-free issue codes for one parsed entry, using the production
// sub-validators (no method is called on a value without a type guard).
export function auditEntryCodes(parsed) {
  const codes = [];
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return ["not-object"];
  }
  if (parsed.cacheSchema === undefined) codes.push("missing-schema");
  else if (parsed.cacheSchema !== CACHE_SCHEMA) codes.push("unknown-schema");
  if (typeof parsed.cachedAt !== "number" || !Number.isFinite(parsed.cachedAt))
    codes.push("invalid-cachedAt");
  if (!validOwner(parsed.cacheOwner)) codes.push("invalid-owner-shape");
  for (const k of Object.keys(parsed))
    if (!TOP_FIELDS.has(k)) codes.push("unexpected-top-path");
  if (parsed.data !== undefined) {
    if (
      !parsed.data ||
      typeof parsed.data !== "object" ||
      Array.isArray(parsed.data)
    ) {
      codes.push("invalid-data-container");
    } else {
      for (const [k, v] of Object.entries(parsed.data)) {
        if (!SUMMARY_FIELD_SET.has(k)) codes.push("unexpected-data-path");
        else if (k === "counts") {
          if (!v || typeof v !== "object" || Array.isArray(v))
            codes.push("invalid-counts-container");
          else {
            for (const [ck, cv] of Object.entries(v)) {
              if (!COUNT_KEY_SET.has(ck)) codes.push("invalid-count-key");
              if (typeof cv !== "number" || !Number.isFinite(cv))
                codes.push("invalid-count-value");
            }
          }
        } else if (!isScalar(v)) codes.push("invalid-scalar-type");
      }
    }
  }
  if (parsed.services !== undefined) {
    if (!Array.isArray(parsed.services))
      codes.push("invalid-services-container");
    else {
      for (const svc of parsed.services) {
        if (!svc || typeof svc !== "object" || Array.isArray(svc))
          codes.push("invalid-service-entry");
        else
          for (const [k, v] of Object.entries(svc)) {
            if (!SERVICE_FIELD_SET.has(k))
              codes.push("unexpected-service-path");
            else if (!isScalar(v)) codes.push("invalid-service-scalar-type");
          }
      }
    }
  }
  if (
    parsed.selectedServiceId !== undefined &&
    typeof parsed.selectedServiceId !== "string"
  )
    codes.push("invalid-selectedServiceId");
  return [...new Set(codes)];
}

/**
 * Audit every sensitive cache entry against the production validator + current
 * owner. Value-free aggregate result only.
 */
export function auditAggregateCache(owner, storage) {
  const store = resolveStorage(storage);
  if (!store) return { entryCount: 0, schemaStatus: "NO CACHE YET" };
  const keys = [];
  for (let i = 0; i < store.length; i += 1) {
    const k = store.key(i);
    if (isSensitiveCacheKey(k)) keys.push(k);
  }
  const results = keys.map((k) => {
    let parsed = null;
    try {
      parsed = JSON.parse(store.getItem(k));
    } catch {
      return { valid: false, codes: ["unparseable"], ownerMatch: false };
    }
    return {
      // schema/structure validity (ownership is reported separately below)
      valid: validateAggregateEntry(parsed, null),
      codes: auditEntryCodes(parsed),
      ownerMatch: owner
        ? ownerMatches(parsed && parsed.cacheOwner, owner)
        : null,
      mismatchReason: owner
        ? ownerMismatchReason(parsed && parsed.cacheOwner, owner)
        : null,
    };
  });
  const owners = results.map((r) => r.ownerMatch);
  const reasonCounts = {
    orgId: 0,
    userId: 0,
    capability: 0,
    "malformed-owner": 0,
  };
  for (const r of results) {
    if (r.mismatchReason && reasonCounts[r.mismatchReason] !== undefined) {
      reasonCounts[r.mismatchReason] += 1;
    }
  }
  return {
    entryCount: results.length,
    validEntryCount: results.filter((r) => r.valid).length,
    invalidEntryCount: results.filter((r) => !r.valid).length,
    ownerMatchesCurrentContext:
      results.length > 0 && owners.every((m) => m === true),
    ownerMismatchCount: owners.filter((m) => m === false).length,
    ownerMismatchReasons: reasonCounts,
    ownersConsistent:
      results.length === 0 || owners.every((m) => m === owners[0]),
    issueCodes: [...new Set(results.flatMap((r) => r.codes))],
    schemaStatus:
      results.length === 0
        ? "NO CACHE YET"
        : results.every((r) => r.valid)
          ? "aggregate-v1 OK"
          : "INVALID",
  };
}

// Preview/development only. Gated by REACT_APP_ENABLE_CACHE_AUDIT so the
// production bundle exposes no diagnostic global and no ownership object.
let _auditOwner = null;
export function registerAuditOwner(owner) {
  _auditOwner = owner;
}
// NOTE: the flag is compared directly (no extra guards) so the CRA/Define
// build replaces it with a literal and dead-code-eliminates this whole block in
// the production bundle (which sets no flag).
if (
  process.env.REACT_APP_ENABLE_CACHE_AUDIT === "1" &&
  typeof window !== "undefined"
) {
  // Owner is held in closure and never exposed as a readable global.
  window.__lightlyAuditAggregateCache = () => auditAggregateCache(_auditOwner);
}
